/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  EntityId,
  IChartingLibraryWidget,
} from 'modules/tw-chart/charting_library';
import { useChartEffect } from 'modules/smart-trade-terminal/helpers/use-chart-effect';
import {
  DexOrderStatus,
  DexTransaction,
  OpenPositionListItem,
} from 'api/types/httpsTypes/dex-trade';
import { useRef, useState } from 'react';
import { safeBig } from 'utils/FormatNumber';

function calculateY(
  tx: DexTransaction,
  currencyIsUsd: boolean,
  isPriceShown: boolean,
  isBuy: boolean
) {
  const { priceInUsd, marketCap, priceEthInUsd, amountIn, amountOut } = tx;

  // USD-based display
  if (currencyIsUsd) {
    return isPriceShown ? priceInUsd : marketCap;
  }

  // For non-USD mode, we need valid amounts
  if (!amountIn || !amountOut) return;

  // Price mode in quote
  if (isPriceShown) {
    const aI = safeBig(amountIn);
    const aO = safeBig(amountOut);
    return isBuy ? aI.div(aO) : aO.div(aI);
  }

  // MarketCap mode in quote
  if (!priceEthInUsd) return;
  return safeBig(marketCap).div(priceEthInUsd);
}

export function useDrawEntries(
  chart: IChartingLibraryWidget | null,
  position: OpenPositionListItem | null,
  interval: string,
  currencyIsUsd: boolean,
  isPriceShown: boolean
) {
  const boughtMarkerIdsRef = useRef<EntityId[]>([]);
  const soldMarkerIdsRef = useRef<EntityId[]>([]);

  useChartEffect(
    chart,
    (widget) => {
      if (!position || !position.transactions) return;

      const chartInstance = widget.activeChart();

      const clearMarkers = () => {
        boughtMarkerIdsRef.current.forEach((id) =>
          chartInstance.removeEntity(id)
        );
        soldMarkerIdsRef.current.forEach((id) =>
          chartInstance.removeEntity(id)
        );
        boughtMarkerIdsRef.current = [];
        soldMarkerIdsRef.current = [];
      };

      const updateMarkers = (from?: number, to?: number) => {
        const visibleRange = chartInstance.getVisibleRange();
        if (!visibleRange?.from || !visibleRange?.to) return;

        const fromTimestamp = from ?? visibleRange.from;
        const toTimestamp = to ?? visibleRange.to;

        // Clear previous markers
        clearMarkers();

        const successBuyTxs = position.transactions.filter(
          (x) =>
            x.side === 'BUY' &&
            (x.status === DexOrderStatus.confirmedSuccess ||
              x.status === DexOrderStatus.confirmedSuccessInProgress)
        );

        const successSaleTxs = position.transactions.filter(
          (x) =>
            x.type.includes('CLOSE_POSITION') &&
            x.side === 'SELL' &&
            (x.status === DexOrderStatus.confirmedSuccess ||
              x.status === DexOrderStatus.confirmedSuccessInProgress)
        );

        successBuyTxs.forEach((tx) => {
          if (!tx.blockDatetime || !tx.priceInUsd) return;

          const txTimestamp = Math.floor(
            new Date(tx.blockDatetime).getTime() / 1000
          );

          const price = calculateY(tx, currencyIsUsd, isPriceShown, true);

          if (!price) return;

          if (txTimestamp >= fromTimestamp && txTimestamp <= toTimestamp) {
            const markerId = chartInstance.createShape(
              {
                price: safeBig(price).round(15).toNumber(),
                time: txTimestamp,
                channel: 'open',
              },
              {
                disableSave: true,
                icon: 0xf111,
                lock: true,
                shape: 'icon',
                zOrder: 'top',
                filled: true,
                overrides: {
                  color: 'rgba(136, 214, 147, 1)',
                  linecolor: 'rgba(136, 214, 147, 1)',
                  backgroundColor: 'rgba(136, 214, 147, 1)',
                  size: 20,
                },
              }
            );
            if (markerId) {
              boughtMarkerIdsRef.current.push(markerId);
            }
          }
        });

        successSaleTxs.forEach((tx) => {
          if (!tx.blockDatetime || !tx.priceInUsd) return;

          const txTimestamp = Math.floor(
            new Date(tx.blockDatetime).getTime() / 1000
          );

          const price = calculateY(tx, currencyIsUsd, isPriceShown, false);

          if (!price) return;

          if (txTimestamp >= fromTimestamp && txTimestamp <= toTimestamp) {
            const markerId = chartInstance.createShape(
              {
                price: safeBig(price).round(15).toNumber(),
                time: txTimestamp,
                channel: 'open',
              },
              {
                disableSave: true,
                icon: 0xf111,
                lock: true,
                shape: 'icon',
                zOrder: 'top',
                overrides: {
                  color: 'rgba(240, 72, 102, 1)',
                  linecolor: 'rgba(240, 72, 102, 1)',
                  backgroundColor: 'rgba(240, 72, 102, 1)',
                  size: 20,
                },
              }
            );
            if (markerId) {
              soldMarkerIdsRef.current.push(markerId);
            }
          }
        });
      };

      // Initial marker draw
      updateMarkers();

      // Subscribe to visible range changes
      chartInstance
        .onVisibleRangeChanged()
        .subscribe(null, ({ from, to }) => updateMarkers(from, to));

      // Cleanup on effect disposal
      return () => {
        clearMarkers();
      };
    },
    [chart, position, interval, isPriceShown, currencyIsUsd]
  );
}
